import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, FileText, GitBranch, Layers } from "lucide-react";
import { PageHeader } from "@/components/uiext";
import { SectionHelp } from "@/lib/help.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Skeleton } from "@/components/ui/skeleton";
import { renderMarkdown } from "@/lib/markdown";

/**
 * The markdown renderer was extracted to `@/lib/markdown` so the public
 * Terms / Privacy pages and the re-consent modal can reuse it without
 * duplication. The transform supports headings, paragraphs, lists, tables,
 * code fences, blockquotes, inline code, bold and links. No mermaid yet.
 */

export default function DocsPage() {
  const { t } = useTranslation();
  const { scope } = useAuth();
  // Tech + Schematics are implementation details of the softswitch (call
  // flow, DB schema, operational runbook). Customers must not see them.
  const isPlatform = scope === "platform";
  const TABS = isPlatform ? ["user", "tech", "schematic"] : ["user"];

  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem("elswitch.docsTab") || "user";
    // If a platform admin previously viewed the tech tab, then someone
    // logged in as a tenant user on the same browser, fall back to 'user'.
    return TABS.includes(saved) ? saved : "user";
  });
  const [docs, setDocs] = useState({ user: null, tech: null, schematic: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("elswitch.docsTab", tab);
  }, [tab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only fetch the manuals this role is allowed to read — tenant users
        // skip the tech + schematic endpoints (they'd get 403 anyway).
        const reqs = {
          user: api.get("/docs/user"),
        };
        if (isPlatform) {
          reqs.tech = api.get("/docs/tech");
          reqs.schematic = api.get("/docs/schematic");
        }
        const keys = Object.keys(reqs);
        const values = await Promise.all(keys.map((k) => reqs[k]));
        if (cancelled) return;
        const next = { user: null, tech: null, schematic: null };
        keys.forEach((k, i) => {
          next[k] = values[i].data.markdown;
        });
        setDocs(next);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatform]);

  const rendered = useMemo(
    () => ({
      user: renderMarkdown(docs.user),
      tech: renderMarkdown(docs.tech),
      schematic: renderMarkdown(docs.schematic),
    }),
    [docs],
  );

  return (
    <div data-testid="page-docs">
      <PageHeader
        title={t("pages.docs.title", "Documentation")}
        subtitle={t(
          "pages.docs.subtitle",
          isPlatform
            ? "User guide, technical manual, and system schematics — all in one place."
            : "User guide — your reference for running calls, managing trunks and SIP users.",
        )}
        icon={BookOpen}
      />
      <SectionHelp
        text={t(
          "pages.docs.tutorial",
          "Turn on Help mode (💡 in the header) to show inline explanations across all pages. Use this Docs page as your offline reference.",
        )}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="user" className="gap-1.5" data-testid="tab-user">
            <FileText className="h-4 w-4" />
            {t("pages.docs.tabs.user", "User manual")}
          </TabsTrigger>
          {isPlatform && (
            <TabsTrigger value="tech" className="gap-1.5" data-testid="tab-tech">
              <Layers className="h-4 w-4" />
              {t("pages.docs.tabs.tech", "Tech manual")}
            </TabsTrigger>
          )}
          {isPlatform && (
            <TabsTrigger
              value="schematic"
              className="gap-1.5"
              data-testid="tab-schematic"
            >
              <GitBranch className="h-4 w-4" />
              {t("pages.docs.tabs.schematic", "Schematics")}
            </TabsTrigger>
          )}
        </TabsList>
        {TABS.map((k) => (
          <TabsContent key={k} value={k} className="mt-0">
            <Card>
              <CardContent className="p-5 max-w-none">
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ) : (
                  rendered[k] || (
                    <div className="text-sm text-muted-foreground">
                      {t("pages.docs.unavailable", "Documentation not loaded.")}
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
